import React, {memo, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import styled, {css} from 'styled-components';
import {
    selectPlayerAudio,
    selectVideoQuality,
} from '../../store/selectors/player';
import * as PlayerActions from '../../store/actions/player';
import {getCurrentLang} from '../../utils/i18n';

const SettingState = {
    HOME: 'home',
    QUALITY: 'quality',
    LANGUAGE: 'language',
};

const ROW_HEIGHT = 46;
const ITEM_ROW_HEIGHT = 36;

function Settings({player, isLive}) {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const [videoQualities, setVideoQualities] = React.useState([]);
    const [audios, setAudios] = React.useState([]);
    const [state, setState] = React.useState(SettingState.HOME);
    const preferedBitrate = useSelector(selectVideoQuality);
    const [selectedQuality, setSelectedQuality] = React.useState(null);
    const defaultLang = getCurrentLang() === 'es' ? 'SPA' : 'POR';

    const preferedAudioLanguage = useSelector(selectPlayerAudio) || defaultLang;
    const [selectedAudioLanguage, setSelectedAudioLanguage] = React.useState(
        null,
    );

    useEffect(() => {
        if (player) {
            const qualities = [
                {id: 'auto', label: 'Auto', bitrate: 1000000000},
                ...player
                    .getAvailableVideoQualities()
                    .sort((q1, q2) => q2.bitrate - q1.bitrate),
            ];

            setVideoQualities(qualities);
            let audios = player.getAvailableAudio();
            setAudios(audios);
            //find audio with same language, or first one
            var audio = audios.find(
                audio => audio.lang === preferedAudioLanguage,
            );
            if (!audio && audios.length > 0) {
                audio = audios[0];
            }
            if (audio) {
                setSelectedAudioLanguage(audio.lang);
            }

            //find nearest bitrate
            if (!!preferedBitrate) {
                const nearestQuality = qualities.reduce((prev, curr) => {
                    return Math.abs(curr.bitrate - preferedBitrate) <
                        Math.abs(prev.bitrate - preferedBitrate)
                        ? curr
                        : prev;
                }, qualities[0]);
                player.setVideoQuality(nearestQuality.id);
                setSelectedQuality(nearestQuality);
            } else {
                setSelectedQuality(qualities[0]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [player]);

    const goBackHome = () => {
        setState(SettingState.HOME);
    };

    const openQuality = () => {
        setState(SettingState.QUALITY);
    };

    const openLanguage = () => {
        setState(SettingState.LANGUAGE);
    };

    const getContainerHeight = state => {
        switch (state) {
            case SettingState.QUALITY:
                return (
                    ITEM_ROW_HEIGHT * videoQualities.length + ROW_HEIGHT + 16
                );
            case SettingState.LANGUAGE:
                return ITEM_ROW_HEIGHT * audios.length + ROW_HEIGHT + 16;
            default:
                return ROW_HEIGHT * (audios.length > 0 ? 2 : 1);
        }
    };

    const selectQuality = quality => {
        player.setVideoQuality(quality.id);
        setSelectedQuality(quality);
        dispatch(PlayerActions.setVideoQuality(quality.bitrate));
        goBackHome();
    };

    const selectAudio = audio => {
        player.setAudio(audio.id);
        dispatch(PlayerActions.setAudio(audio.lang));
        setSelectedAudioLanguage(audio.lang);
        goBackHome();
    };

    return (
        <Container height={getContainerHeight(state)} isLive={isLive}>
            <RowGroup
                visible={state === SettingState.HOME}
                height={getContainerHeight(SettingState.HOME)}
                primary
            >
                <Row
                    title={t('playerui.settings.qualityTitle')}
                    value={niceBitrate(selectedQuality)}
                    showLine={audios.length > 0}
                    onClick={openQuality}
                />
                {audios.length > 0 && (
                    <Row
                        title={t('playerui.settings.languageTitle')}
                        value={selectedAudioLanguage}
                        onClick={openLanguage}
                    />
                )}
            </RowGroup>

            <RowGroup
                visible={state === SettingState.QUALITY}
                height={getContainerHeight(SettingState.QUALITY)}
            >
                <HeaderBack
                    title={t('playerui.settings.qualityTitle')}
                    onClick={goBackHome}
                />
                <Line marginBottom={4} />
                {videoQualities.map((quality, index) => (
                    <ItemRow
                        title={quality.label}
                        selected={selectedQuality.id === quality.id}
                        onSelect={() => selectQuality(quality)}
                        key={quality.id}
                    />
                ))}
            </RowGroup>

            <RowGroup
                visible={state === SettingState.LANGUAGE}
                height={getContainerHeight(SettingState.LANGUAGE)}
            >
                <HeaderBack
                    title={t('playerui.settings.languageTitle')}
                    onClick={goBackHome}
                />
                <Line marginBottom={4} />
                {audios.map((audio, index) => (
                    <ItemRow
                        title={audio.label}
                        selected={audio.label === selectedAudioLanguage}
                        key={audio.id}
                        onSelect={() => selectAudio(audio)}
                    />
                ))}
            </RowGroup>
        </Container>
    );
}

const Container = styled.div`
    background-color: black;
    opacity: 0.65;
    border-radius: 6px;
    position: absolute;
    left: 25px;
    bottom: ${({isLive}) => (isLive ? 80 : 120)}px;
    overflow: hidden;
    transition: all 0.3s ease-in-out;
    width: 260px;
    height: ${props => props.height}px;
`;

const RowGroup = styled.div`
    display: flex;
    flex-direction: column;
    transition: all 0.3s ease-in-out;
    width: 260px;
    height: ${props => props.height}px;
    position: absolute;
    bottom: 0;
    left: ${props => (props.visible ? 0 : props.primary ? -260 : 260)}px;
`;

const Row = ({title, value, showLine, onClick}) => {
    return (
        <RowContainer onClick={onClick}>
            <ContentContainer>
                <Title>{title}</Title>
                <Value>{value}</Value>
                <Chevron />
            </ContentContainer>
            {showLine && <Line />}
        </RowContainer>
    );
};

const RowContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const ContentContainer = styled.div`
    display: flex;
    flex-direction: row;
    padding: 10px 20px;
    &:hover {
        background-color: #333333;
    }
    margin: 4px 0;
    cursor: pointer;
    align-items: center;
`;

const Title = styled.div`
    font-family: AvenirNextRoundedStd, Helvetica, Arial, sans-serif;
    font-weight: 500;
    font-size: 14px;
    color: white;
    flex: 1;
`;

const Value = styled(Title)`
    font-weight: 700;
    flex: 0;
    margin-right: 10px;
`;

const Line = styled.div`
    height: 1px;
    background-color: #333333;
    margin: 0px 20px;
    ${props => props.marginBottom && `margin-bottom: ${props.marginBottom}px;`}
`;

const ItemRow = ({title, selected, onSelect}) => {
    return (
        <ItemRowContainer selected={selected} onClick={onSelect}>
            <Check selected={selected} />
            {title}
        </ItemRowContainer>
    );
};

const ItemRowContainer = styled.div`
    display: flex;
    flex-direction: row;
    padding: 8px 20px;
    &:hover {
        background-color: #333333;
    }
    font-size: 14px;
    ${props => props.selected && 'font-weight: 700;'}
    cursor: pointer;
`;

const Check = styled.img.attrs({
    src: require('../../assets/ic_tick.png'),
})`
    width: 20px !important;
    height: 20px !important;
    opacity: ${props => (props.selected ? 1 : 0)};
    margin-right: 4px;
`;

const HeaderBack = ({title, onClick}) => {
    return (
        <ContentContainer onClick={onClick}>
            <Chevron left />
            <Title>{title}</Title>
        </ContentContainer>
    );
};

const Chevron = styled.img.attrs({
    src: require('../../assets/ic_chevron_white.png'),
    alt: 'select',
})`
    width: 8px;
    ${props =>
        props.left &&
        css`
            transform: rotate(180deg);
            margin-right: 10px;
        `}
`;

const niceBitrate = quality => {
    if (!quality || quality.id === 'auto') {
        return 'Auto';
    }

    return `${Math.round(quality.bitrate / 1000)}kbps`;
};

export default memo(Settings);
